import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { polyfill as scrollPolyfill } from "seamless-scroll-polyfill";
import { Box, Typography, useTheme, Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// import ScrollableWrapper from "../ScrollableWrapper";
import { SelectedSummary } from "../SelectedSummary";
import { ExportOptions } from "../ExportOptions";
import { OptionGroup } from "../OptionGroup";
import {
  InteractiveButtonsRoot,
  PanelContent,
  ContentTop,
  ContentBottom,
  CategorySection,
  CategoryHeader,
  SubCategoryRow,
  OptionGrid,
  ToggleMenuBtn,
} from "./InteractiveButtons.styles";
import { findByDescriptor } from "../../helpers/findItems";
// import { generateRandomConfiguration } from "../../helpers/randomizer";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";
import { useRouteParams } from "../../context/RouteParamsContext";
import { parseUrlConfigString } from "../../helpers/config-utilities";
import { useDevice } from "../../hooks/useDevice";

/* ---------------- helpers ---------------- */

function normalizeDescriptors(list = []) {
  const out = [];
  for (const d of list) {
    if (!d || typeof d !== "object") continue;
    const keys = Object.keys(d);
    if (keys.length !== 1) continue;
    const key = keys[0];
    out.push({ key, value: d[key], descriptor: d });
  }
  return out;
}

// parse app response into [{key:..., value:..., descriptor:{key:value}}, ...]
function parseDescriptorsFromAppResponse(resp) {
  if (typeof resp !== "string") return [];
  const out = [];
  try {
    const j = JSON.parse(resp);
    if (j && typeof j === "object") {
      Object.keys(j).forEach((k) => out.push({ [k]: j[k] }));
      return out;
    }
  } catch {}
  const pairs = [
    ...resp.matchAll(/"([A-Za-z0-9_]+)"\s*:\s*(?:"([^"]+)"|([0-9.]+))/g),
  ];
  for (const m of pairs) {
    const key = m[1];
    const val = m[2] ?? m[3];
    const coerced = m[3] ? Number(val) : val;
    out.push({ [key]: coerced });
  }
  return out;
}

/* -------------- component -------------- */

const InteractiveButtons = forwardRef((props, ref) => {
  const {
    emitUIInteraction,
    actionsList,
    className,
    applicationResponse,
    onStartExport,
    showingOverlay,
    onHandleMenuToggle,
    menuOpen,
    isMobile,
  } = props;

  const theme = useTheme();
  const { isPortrait } = useDevice();

  const {
    selectOption,
    map: selectedMap, // single source of truth (hydrated + persisted)
  } = useSelectedOptions();

  const { configCode } = useRouteParams();
  const hasLoadedFromUrl = useRef(false);

  const rootRef = useRef(null);

  const [expandedCategories, setExpandedCategories] = useState(() =>
    actionsList ? actionsList?.map((_, i) => i) : []
  );
  const toggleCategory = (idx) => {
    setExpandedCategories(
      (prev) =>
        prev.includes(idx)
          ? prev.filter((i) => i !== idx) // collapse
          : [...prev, idx] // expand
    );
  };

  useEffect(() => {
    scrollPolyfill({ duration: 90 });
  }, []);

  // Fallback defaults (only used if app + local are missing a key)
  const DEFAULT_SELECTIONS = useMemo(
    () => [
      { descriptor: { basePrice: true } },
      { descriptor: { WS: "ws01" } },
      { descriptor: { FlCo: "wo01" } },
      { descriptor: { CeCo: "wo01" } },
      { descriptor: { WaCo: "br01" } },
      { descriptor: { ChCo: "gl" } },
      { descriptor: { SoCo: "bl" } },
      { descriptor: { BlCo: "dm" } },
      { descriptor: { ViMo: "el" } },
      { descriptor: { MeWe: "pc" } },
    ],
    []
  );

  useEffect(() => {
    // CRITICAL CHECK 1: Ensure actionsList is loaded (same as before)
    if (!Array.isArray(actionsList) || actionsList?.length === 0) {
      return;
    }

    // CRITICAL CHECK 2: ONLY hydrate if the context state is empty.
    // If selectedMap is NOT empty, it means the context has ALREADY been
    // hydrated by localStorage (from the fix in SelectedOptionsContext.js).
    if (Object.keys(selectedMap).length > 0) {
      return;
    }

    // --- BUILD PRIORITY MAP (Only focusing on App Response and Defaults) ---

    // 1. Highest Priority: applicationResponse
    const descriptorsByGroupKey = {};
    const appDescriptors = parseDescriptorsFromAppResponse(applicationResponse);
    appDescriptors.forEach((desc) => {
      const groupKey = Object.keys(desc)[0];
      if (groupKey) descriptorsByGroupKey[groupKey] = desc;
    });

    // 2. Lowest Priority: DEFAULT_SELECTIONS (Only used if no App Response)
    DEFAULT_SELECTIONS.forEach((item) => {
      const desc = item.descriptor;
      const groupKey = Object.keys(desc)[0];
      // Only fill groups not already set by appResponse
      if (groupKey && !descriptorsByGroupKey[groupKey]) {
        descriptorsByGroupKey[groupKey] = desc;
      }
    });

    // --- HYDRATE CONTEXT ---
    const descriptorsToSelect = Object.values(descriptorsByGroupKey);

    descriptorsToSelect.forEach((descriptor) => {
      // Find the full option object
      const fullOption = findByDescriptor(actionsList, descriptor);

      if (fullOption) {
        selectOption(fullOption);
      }
    });
  }, [
    DEFAULT_SELECTIONS,
    actionsList,
    applicationResponse,
    selectOption,
    selectedMap,
  ]);

  const [mainActions, setMainActions] = useState([]);

  // split actionsList into mainActions
  useEffect(() => {
    const arr = [];
    actionsList?.forEach((item) => arr.push(item));
    setMainActions(arr);
  }, [actionsList]);

  useEffect(() => {
    const orientation = isPortrait ? { zoom: "-37.5" } : { zoom: "0" };
    clickOnAction(orientation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortrait]);

  const clickOnAction = useCallback(
    (descriptor) => {
      if (typeof emitUIInteraction === "function") {
        emitUIInteraction(descriptor);
      } else {
        console.warn("emitUIInteraction is not ready yet", emitUIInteraction);
      }
    },
    [emitUIInteraction]
  );

  useEffect(() => {
    // 🛑 GUARD 1: Block loading if the overlay is showing
    if (showingOverlay) return;

    // 🛑 GUARD 2: Block if already processed from URL
    if (hasLoadedFromUrl.current) return;

    // 🛑 GUARD 3: No config code? Nothing to load.
    if (!configCode) {
      hasLoadedFromUrl.current = true;
      return;
    }

    // 1️⃣ Parse the configuration string into { groupKey: value }
    const descriptorMap = parseUrlConfigString(configCode);
    if (Object.keys(descriptorMap).length === 0) {
      hasLoadedFromUrl.current = true;
      return;
    }

    // Helper — detect "r=...,g=...,b=...,a=..." color values
    const isFloatRgbaString = (val) =>
      typeof val === "string" &&
      /^r=\d*\.?\d+,\s*g=\d*\.?\d+,\s*b=\d*\.?\d+,\s*a=\d*\.?\d+$/i.test(val);

    // Helper — find the RGB/IPF node for a given group
    function findCustomRgbNode(actionsList, groupKey) {
      const ipfMatch = findByDescriptor(actionsList, { [groupKey]: "ipf" });
      if (ipfMatch?.node) return ipfMatch;

      // fallback: manually scan for nodes marked customRGB
      const stack = [...(actionsList || [])];
      while (stack.length) {
        const n = stack.pop();
        if (n?.subActions?.length) stack.push(...n.subActions);
        if (n?.action?.descriptor && n?.customRGB === true) {
          const key = Object.keys(n.action.descriptor)[0];
          if (key === groupKey) return { node: n, sub: null };
        }
      }
      return null;
    }

    // 2️⃣ Loop over each descriptor and apply it
    Object.entries(descriptorMap).forEach(([groupKey, descriptorValue]) => {
      const currentOption = selectedMap[groupKey];
      const currentDescriptorValue = currentOption?.descriptor?.[groupKey];

      // Normalize boolean strings
      let normalizedDescriptorValue = descriptorValue;
      if (normalizedDescriptorValue === "true")
        normalizedDescriptorValue = true;
      if (normalizedDescriptorValue === "false")
        normalizedDescriptorValue = false;

      const isFloat = isFloatRgbaString(normalizedDescriptorValue);

      // Skip redundant updates
      if (isFloat) {
        if (currentOption?.color === normalizedDescriptorValue) return;
      } else {
        if (currentDescriptorValue === normalizedDescriptorValue) return;
      }

      // Find the full option in the actions list
      const descriptor = { [groupKey]: normalizedDescriptorValue };
      let optionContext = findByDescriptor(actionsList, descriptor);

      // If not found, check if this is a float RGBA color
      if (!optionContext && isFloat) {
        optionContext = findCustomRgbNode(actionsList, groupKey);
      }

      if (optionContext?.node) {
        const { node, sub } = optionContext;

        // ✅ Store the float RGBA as the descriptor value (not "ipf")
        const storedDescriptor = isFloat
          ? { [groupKey]: normalizedDescriptorValue }
          : node.action?.descriptor || descriptor;

        // What to send to Unreal
        const engineDescriptor = isFloat
          ? { [groupKey]: normalizedDescriptorValue }
          : storedDescriptor;

        const payload = {
          name: node.name,
          image: node.image,
          price: node.price,
          descriptor: storedDescriptor,
          baseOption: node.action?.baseOption ?? node.baseOption,
          inSummary: node.action?.inSummary ?? node.inSummary,
          subCategory: sub?.name,
          groupKey,
          ...(isFloat ? { color: normalizedDescriptorValue } : {}),
        };

        // A) Update context
        selectOption(payload);

        // B) Send to Unreal Engine
        clickOnAction?.(engineDescriptor);
      }
    });

    // 3️⃣ Prevent this effect from running again
    hasLoadedFromUrl.current = true;
  }, [
    configCode,
    selectOption,
    actionsList,
    clickOnAction,
    selectedMap,
    showingOverlay,
  ]);

  // at top:
  const didHydrateRef = React.useRef(false);

  // REPLACE your current hydration useEffect with this:
  useEffect(() => {
    const hasActions = Array.isArray(mainActions) && mainActions.length > 0;
    if (!hasActions || didHydrateRef.current) return;

    // lowest: defaults
    const defaultsArr = DEFAULT_SELECTIONS.map((d) => d.descriptor);

    // middle: selectedOptions_v1 (persisted context)
    let lsArr = [];
    try {
      const lsObj = JSON.parse(
        localStorage.getItem("selectedOptions_v1") || "{}"
      );
      lsArr = Object.values(lsObj)
        .map((e) => e.descriptor)
        .filter(Boolean);
    } catch {}

    // normalize:
    const nDefaults = normalizeDescriptors(defaultsArr);
    const nLS = normalizeDescriptors(lsArr);

    // merge (defaults -> local) later wins
    const merged = new Map();
    for (const it of nDefaults) merged.set(it.key, it);
    for (const it of nLS) merged.set(it.key, it);

    // seed context
    merged.forEach(({ descriptor }) => {
      const name = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.name,
      });
      const image = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.image,
      });
      const price = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.price,
      });
      const subCategory =
        findByDescriptor(mainActions, descriptor, {
          select: ({ sub }) => sub?.name,
        }) || "";
      const inSummary = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.inSummary,
      });
      const baseOption = findByDescriptor(mainActions, descriptor, {
        select: ({ sub }) => sub?.baseOption,
      });

      selectOption({
        name,
        image,
        price,
        baseOption,
        descriptor,
        subCategory,
        inSummary,
      });
    });

    didHydrateRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainActions]); // <-- IMPORTANT: no applicationResponse, no selectedMap, no selectOption

  const lastAppRespRef = React.useRef(null);

  // ####################################################
  // fix this later
  useEffect(() => {
    if (!didHydrateRef.current) return; // wait until we seeded from defaults/LS
    if (lastAppRespRef.current === applicationResponse) return;

    const appArr = parseDescriptorsFromAppResponse(applicationResponse);

    if (!appArr.length) {
      lastAppRespRef.current = applicationResponse || null;
      return;
    }
    const nApp = normalizeDescriptors(appArr);
    const changed = [];

    for (const it of nApp) {
      const key = it.key;
      const newVal = it.value;
      const descriptor = { [key]: newVal };

      // 1) If we already have this group in context/localStorage,
      //    keep everything and only update the descriptor (+ color for FloatRGB).
      const existing = selectedMap?.[key]; // shape stored in selectedOptions_v1

      if (existing) {
        const keep = { ...existing, descriptor };

        // If UE sent a FloatRGB (e.g. "r=0.086,g=0.710,b=0.439,a=1"),
        // persist it so the ColorWheel can position itself.
        if (typeof newVal === "string" && newVal.startsWith("r=")) {
          keep.color = newVal;
        }

        changed.push(keep);
        continue;
      }

      // 2) Otherwise, try to resolve the full option from the tree.
      const found = findByDescriptor(mainActions, descriptor); // { node, sub } or null
      const name = found?.node?.name ?? null;
      const image = found?.node?.image ?? null;
      const price = found?.node?.price ?? 0;
      const subCategory = found?.sub?.name ?? "";
      const inSummary =
        found?.node?.inSummary ?? found?.node?.action?.inSummary ?? null;
      const baseOption =
        found?.node?.baseOption ?? found?.node?.action?.baseOption ?? null;

      const payload = {
        name,
        image,
        price,
        baseOption,
        descriptor,
        subCategory,
        inSummary,
      };

      if (typeof newVal === "string" && newVal.startsWith("r=")) {
        payload.color = newVal;
      }

      changed.push(payload);
    }

    if (changed.length) {
      changed.forEach(selectOption);
    }

    lastAppRespRef.current = applicationResponse || null;
  }, [applicationResponse, mainActions, selectedMap, selectOption]);

  // ——— derive UI stuff from context ———

  // ####################################################

  // const checkIfOptionIsActive = (option) => {
  //   if (!option || typeof option !== "object") return false;
  //   const [key, value] = Object.entries(option)[0] || [];
  //   const selectedVal = selectedMap?.[key]?.descriptor?.[key];
  //   return JSON.stringify(selectedVal) === JSON.stringify(value);
  // };

  // const selectAndEmit = (descriptor) => {
  //   if (!descriptor || typeof descriptor !== "object") return;

  //   // 1) Send to UE
  //   clickOnAction(descriptor);

  //   // 2) Find the full node for this descriptor in your tree
  //   const found = findByDescriptor(actionsList, descriptor);
  //   if (!found || !found.node) return; // not a leaf or not found → nothing to store

  //   const { node, sub } = found;

  //   // 3) Build the same shape OptionGroup uses for selectOption
  //   selectOption({
  //     name: node.name,
  //     image: node.image,
  //     price: node.price ?? 0,
  //     descriptor: node.action?.descriptor || descriptor,
  //     baseOption: node.action?.baseOption ?? node.baseOption,
  //     inSummary: node.action?.inSummary ?? node.inSummary,
  //     subCategory: sub?.name || "",
  //   });
  // };

  return (
    <>
      <ToggleMenuBtn
        onClick={() => onHandleMenuToggle(!menuOpen)}
        show={menuOpen}
        isMobile={isMobile}
      >
        <ExpandMoreIcon fontSize="large" />
      </ToggleMenuBtn>
      <InteractiveButtonsRoot
        className={className}
        show={menuOpen}
        ref={rootRef}
      >
        <PanelContent show={menuOpen}>
          <ContentBottom>
            <SelectedSummary
              onHandleToggle={onHandleMenuToggle}
              showInterface={menuOpen}
            />
          </ContentBottom>
          <ContentTop>
            {/* --- Collapsible Categories --- */}
            {(mainActions ?? []).map((category, cIdx) => {
              const expanded = expandedCategories.includes(cIdx);
              return (
                <CategorySection key={`cat-${cIdx}`}>
                  <CategoryHeader onClick={() => toggleCategory(cIdx)}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 500,
                        color: theme?.palette?.companyColors?.secondary,
                      }}
                    >
                      {category.name}
                    </Typography>
                    <Box
                      sx={{
                        transition: "transform 0.3s ease-in-out",
                      }}
                    >
                      {expanded ? (
                        <RemoveIcon fontSize="large" />
                      ) : (
                        <AddIcon fontSize="large" />
                      )}
                    </Box>
                  </CategoryHeader>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {category?.name === "Export Image" && (
                      <ExportOptions
                        clickOnAction={clickOnAction}
                        onStartExport={onStartExport}
                      />
                    )}

                    {(category?.subActions ?? []).map((sub, sIdx) => {
                      return (
                        <SubCategoryRow key={`sub-${cIdx}-${sIdx}`}>
                          <OptionGrid>
                            {(sub?.subActions ?? []).map((optGroup, iIdx) => (
                              <OptionGroup
                                key={`opt-${cIdx}-${sIdx}-${iIdx}`}
                                title={optGroup?.name}
                                items={optGroup.subActions ?? []}
                                clickOnAction={clickOnAction}
                                onSelectOption={(payload) =>
                                  selectOption(payload)
                                }
                                selectedMap={selectedMap}
                                subCategoryName={sub.name}
                              />
                            ))}
                          </OptionGrid>
                        </SubCategoryRow>
                      );
                    })}
                  </Collapse>
                </CategorySection>
              );
            })}
          </ContentTop>
        </PanelContent>
      </InteractiveButtonsRoot>
    </>
  );
});

InteractiveButtons.defaultProps = { open: false };

export { InteractiveButtons };
export default InteractiveButtons;
